import type { AgentRef, AgentRunRef } from "@/types/agent-summary"
import {
  AgentRunContentResponse,
  AgentRunFeedbackKind,
  AgentRunFeedbackResponse,
  AgentRunFeedResponse,
  AgentWatchingResponse,
} from "@/types/custom-agent"

import type { RunEvent } from "@/lib/agent-run-progress"
import { now } from "@/lib/date-utils"
import type { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { agentsApi } from "./agents-api"
import { baseApi } from "./base-api"

export interface AgentRunFeedQueryArgs {
  agentRef?: AgentRef
  /** Home carousel: five for one agent, two for a few, one once there are five or more. */
  home?: boolean
}

// The home "Your feed" of agent run results: a lean summary list, and
// per-run tap-through content (a union keyed by what's present; the message
// variant is the report shaped as a BotMessage).
export const agentRunsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgentRunFeed: builder.infiniteQuery<
      AgentRunFeedResponse,
      AgentRunFeedQueryArgs,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.meta.next_page ?? undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/api/agent_runs",
        params: {
          ...(pageParam > 1 ? { page: pageParam } : {}),
          ...(queryArg.agentRef ? { agent_ref: queryArg.agentRef } : {}),
          ...(queryArg.home ? { home: true } : {}),
        },
      }),
    }),
    getAgentWatching: builder.infiniteQuery<
      AgentWatchingResponse,
      void,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.meta.next_page ?? undefined,
      },
      query: ({ pageParam }) => ({
        url: "/api/agent_runs/watching",
        params: pageParam > 1 ? { page: pageParam } : undefined,
      }),
    }),
    // POST: the first tap materializes the run's answer (ephemeral chat +
    // persisted message) server-side; repeat calls return the same pair, so
    // query caching semantics are safe.
    getAgentRunContent: builder.query<AgentRunContentResponse, AgentRunRef>({
      query: (ref) => ({
        url: `/api/agent_runs/${ref}/content`,
        method: "POST",
      }),
    }),
    // The thumbs on a report. An edit starts a rewrite; its progress arrives
    // over the agent_run socket type like any run's.
    sendAgentRunFeedback: builder.mutation<
      AgentRunFeedbackResponse,
      { ref: AgentRunRef; kind: AgentRunFeedbackKind; note?: string }
    >({
      query: ({ ref, kind, note }) => ({
        url: `/api/agent_runs/${ref}/feedback`,
        method: "POST",
        data: { kind, note },
      }),
    }),
    // Dismiss on a Watching card hides the finding, not the watch. The card leaves
    // the cache at once; the server keeps it away until a materially lower price.
    dismissWatchingItem: builder.mutation<void, AgentRunRef>({
      query: (ref) => ({
        url: `/api/agent_runs/${ref}/dismiss`,
        method: "POST",
      }),
      onQueryStarted(ref, { dispatch, queryFulfilled }) {
        const removal = dispatch(
          agentRunsApi.util.updateQueryData(
            "getAgentWatching",
            undefined,
            (draft) => {
              for (const page of draft.pages) {
                page.alerts = page.alerts.filter((alert) => alert.ref !== ref)
              }
            }
          )
        )
        queryFulfilled.catch(removal.undo)
      },
    }),
    // "Delete results" on a report: the run leaves Home's feed.
    deleteAgentRun: builder.mutation<void, AgentRunRef>({
      query: (ref) => ({
        url: `/api/agent_runs/${ref}`,
        method: "DELETE",
      }),
      onQueryStarted(ref, { dispatch, getState, queryFulfilled }) {
        const patches: Array<{ undo: () => void }> = []
        const cachedArgs = agentRunsApi.util.selectCachedArgsForQuery(
          getState(),
          "getAgentRunFeed"
        )
        cachedArgs.forEach((args) => {
          patches.push(
            dispatch(
              agentRunsApi.util.updateQueryData(
                "getAgentRunFeed",
                args,
                (draft) => {
                  for (const page of draft.pages) {
                    page.runs = page.runs.filter((run) => run.ref !== ref)
                  }
                }
              )
            )
          )
        })
        queryFulfilled.catch(() => patches.forEach((patch) => patch.undo()))
      },
    }),
    // Delete on a Watching card stops the price alert. The card leaves the cache
    // at once; Settings' agent list is refreshed on success.
    deleteWatchingItem: builder.mutation<void, AgentRunRef>({
      query: (ref) => ({
        url: `/api/agent_runs/${ref}/watch`,
        method: "DELETE",
      }),
      onQueryStarted(ref, { dispatch, queryFulfilled }) {
        const removal = dispatch(
          agentRunsApi.util.updateQueryData(
            "getAgentWatching",
            undefined,
            (draft) => {
              for (const page of draft.pages) {
                page.alerts = page.alerts.filter((alert) => alert.ref !== ref)
              }
            }
          )
        )
        queryFulfilled
          .then(() => {
            dispatch(agentsApi.util.invalidateTags(["Agent"]))
          })
          .catch(removal.undo)
      },
    }),
  }),
})

export const {
  useGetAgentRunFeedInfiniteQuery,
  useGetAgentWatchingInfiniteQuery,
  useGetAgentRunContentQuery,
  useSendAgentRunFeedbackMutation,
  useDismissWatchingItemMutation,
  useDeleteWatchingItemMutation,
  useDeleteAgentRunMutation,
} = agentRunsApi

/**
 * Folds one agent_run socket event into the cached feed: a run that starts gets
 * an in-progress item at the head, and every later event is appended to its
 * item's events.
 */
function runningCardFinish(
  runs: AgentRunFeedResponse["runs"],
  ref: AgentRunRef
) {
  const card = runs.find((run) => run.ref === ref && run.status === "running")
  if (!card || card.status !== "running") return undefined
  const finished = card.events.find((event) => event.event === "run_finished")
  return {
    feedItem:
      finished?.event === "run_finished" ? finished.feed_item : undefined,
    revisesRef: card.revises_ref,
  }
}

export function applyAgentRunEvent(
  dispatch: AppDispatch,
  getState: () => RootState,
  event: RunEvent
) {
  const cachedArgs = agentRunsApi.util.selectCachedArgsForQuery(
    getState(),
    "getAgentRunFeed"
  )
  cachedArgs.forEach((args) => {
    if (args.agentRef) return
    dispatch(
      agentRunsApi.util.updateQueryData("getAgentRunFeed", args, (draft) => {
        const firstPage = draft.pages[0]
        if (!firstPage) return
        const card = firstPage.runs.find((run) => run.ref === event.run_ref)
        if (card?.status === "running") {
          // A refetch can land an event the socket also delivers.
          const serialized = JSON.stringify(event)
          const seen = card.events.some(
            (known) => JSON.stringify(known) === serialized
          )
          if (!seen) card.events.push(event)
        } else if (!card && event.event === "run_started") {
          firstPage.runs.unshift({
            status: "running",
            ref: event.run_ref,
            agent_ref: event.agent_ref,
            agent_name: event.agent_name,
            revises_ref: event.revises_ref,
            created_at: now(),
            events: [event],
          })
        }
      })
    )
  })
}

/**
 * Ends a finished run's in-progress item: a noteworthy run becomes the newest
 * report, behind any runs still working and taking the place of the report it
 * rewrote, if any; a quiet one just goes.
 */
export function settleAgentRun(
  dispatch: AppDispatch,
  getState: () => RootState,
  ref: AgentRunRef
) {
  const state = getState()
  const cachedArgs = agentRunsApi.util.selectCachedArgsForQuery(
    state,
    "getAgentRunFeed"
  )
  const unfilteredPage = agentRunsApi.endpoints.getAgentRunFeed.select({})(
    state
  ).data?.pages[0]
  const sourceFinish = unfilteredPage
    ? runningCardFinish(unfilteredPage.runs, ref)
    : undefined

  cachedArgs.forEach((args) => {
    dispatch(
      agentRunsApi.util.updateQueryData("getAgentRunFeed", args, (draft) => {
        const firstPage = draft.pages[0]
        if (!firstPage) return

        const localFinish = runningCardFinish(firstPage.runs, ref)
        const card = firstPage.runs.find(
          (run) => run.ref === ref && run.status === "running"
        )
        if (card) {
          firstPage.runs = firstPage.runs.filter((run) => run !== card)
        }

        const feedItem = localFinish?.feedItem ?? sourceFinish?.feedItem
        const revisesRef = localFinish?.revisesRef ?? sourceFinish?.revisesRef
        if (!feedItem || firstPage.runs.some((run) => run.ref === ref)) {
          return
        }
        if (
          args.agentRef &&
          feedItem.agent_ref &&
          feedItem.agent_ref !== args.agentRef
        ) {
          return
        }

        if (revisesRef) {
          firstPage.runs = firstPage.runs.filter(
            (run) => run.ref !== revisesRef
          )
        }
        const firstReport = firstPage.runs.findIndex(
          (run) => run.status === "complete"
        )
        firstPage.runs.splice(
          firstReport === -1 ? firstPage.runs.length : firstReport,
          0,
          feedItem
        )
      })
    )
  })
}
